/**
 * PlatformHealthPage.tsx — Platform health monitoring & operations
 *
 * Platform admin view combining:
 *   - Noisy-neighbor detection (tenants exceeding resource thresholds)
 *   - Provider health (per-tenant Stripe connection status)
 *   - GDPR data export triggers
 *   - Secret rotation controls
 *   - System-wide health metrics
 *
 * @module packages/billing-admin/src/pages/PlatformHealthPage
 * @since 2026-06-20 (Platform Upgrade — Phase 7)
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import PlatformLayout from '../components/PlatformLayout';
import ConfirmDialog from '../components/ConfirmDialog';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface NoisyNeighborReport {
  tenantId: string;
  tenantName?: string;
  flags: string[];
  p95Ms?: number;
  errorRate?: number;
  requestVolume?: number;
}

interface NoisyNeighborResponse {
  detected: number;
  tenants: NoisyNeighborReport[];
  thresholds: { maxP95Ms: number; errorRatePct: number; volumeMultiplier: number };
}

interface TenantListItem {
  id: string;
  name: string;
  slug: string;
  status: string;
  createdAt: string;
  metadata?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function PlatformHealthPage(): React.JSX.Element {
  const { token, apiBaseUrl } = useAdminAuth();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<'noisy' | 'health' | 'gdpr' | 'secrets'>('noisy');
  const [exportTenantId, setExportTenantId] = useState<string | null>(null);
  const [rotateTenantId, setRotateTenantId] = useState<string | null>(null);
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const [showRotateConfirm, setShowRotateConfirm] = useState(false);

  // ─── Noisy Neighbors ───────────────────────────────────────────────────
  const { data: noisyData, isLoading: noisyLoading } = useQuery<NoisyNeighborResponse>({
    queryKey: ['platform', 'noisy-neighbors'],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/platform/noisy-neighbors`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      return res.json();
    },
    enabled: !!token,
    refetchInterval: 30000,
  });

  // ─── Tenants list (for GDPR + rotation) ────────────────────────────────
  const { data: tenantsData } = useQuery<{ tenants: TenantListItem[] }>({
    queryKey: ['platform', 'tenants'],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/platform/tenants`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      return res.json();
    },
    enabled: !!token,
  });

  // ─── GDPR Export Mutation ──────────────────────────────────────────────
  const gdprMutation = useMutation({
    mutationFn: async (tenantId: string) => {
      const res = await fetch(`${apiBaseUrl}/platform/tenants/${tenantId}/gdpr-export`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      const blob = await res.blob();
      // Trigger download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gdpr-export-${tenantId}.json`;
      a.click();
      URL.revokeObjectURL(url);
    },
    onSuccess: () => setShowExportConfirm(false),
  });

  // ─── Secret Rotation Mutation ──────────────────────────────────────────
  const rotateMutation = useMutation({
    mutationFn: async (tenantId: string) => {
      const res = await fetch(`${apiBaseUrl}/platform/tenants/${tenantId}/rotate-secrets`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Rotation failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      setShowRotateConfirm(false);
      queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
    },
  });

  // ─── Tab navigation ────────────────────────────────────────────────────
  const tabs = [
    { key: 'noisy' as const, label: 'Noisy Neighbors', badge: noisyData?.detected },
    { key: 'health' as const, label: 'Provider Health' },
    { key: 'gdpr' as const, label: 'GDPR Export' },
    { key: 'secrets' as const, label: 'Secret Rotation' },
  ];

  return (
    <PlatformLayout title="Platform Health">
      <div className="space-y-6">
        {/* Tab Bar */}
        <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              {tab.label}
              {tab.badge !== undefined && tab.badge > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ─── Noisy Neighbors Tab ────────────────────────────────────── */}
        {activeTab === 'noisy' && (
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">Resource Threshold Alerts</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Tenants exceeding p95 latency, error rate, or volume thresholds
                  </p>
                </div>
                {noisyData && (
                  <div className="text-right text-xs text-slate-500">
                    <p>Max P95: {noisyData.thresholds.maxP95Ms}ms</p>
                    <p>Error Rate: {noisyData.thresholds.errorRatePct}%</p>
                  </div>
                )}
              </div>

              {noisyLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-cyan-500 border-t-transparent" />
                </div>
              ) : !noisyData || noisyData.detected === 0 ? (
                <div className="rounded-lg bg-green-50 border border-green-200 p-4 text-center">
                  <p className="text-sm font-medium text-green-700">All Clear</p>
                  <p className="text-xs text-green-600 mt-0.5">No tenants exceeding resource thresholds.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {noisyData.tenants.map((tenant) => (
                    <div key={tenant.tenantId} className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-800">
                            {tenant.tenantName ?? tenant.tenantId.slice(0, 8)}
                          </p>
                          <div className="mt-1 flex gap-2">
                            {tenant.flags.map((flag) => (
                              <span key={flag} className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                                {flag}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="text-right text-xs text-slate-600">
                          {tenant.p95Ms !== undefined && <p>P95: {tenant.p95Ms}ms</p>}
                          {tenant.errorRate !== undefined && <p>Errors: {tenant.errorRate}%</p>}
                          {tenant.requestVolume !== undefined && <p>Volume: {tenant.requestVolume}/min</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── Provider Health Tab ────────────────────────────────────── */}
        {activeTab === 'health' && (
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-4">Stripe Connect Account Health</h3>
            <div className="overflow-hidden rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-500 uppercase">Tenant</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-500 uppercase">Charges</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-500 uppercase">Last Check</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(tenantsData?.tenants ?? []).filter(t => t.status === 'active').map((tenant) => {
                    const meta = tenant.metadata as Record<string, unknown> | undefined;
                    const healthStatus = meta?.healthStatus as string | undefined;
                    const chargesEnabled = meta?.chargesEnabled as boolean | undefined;
                    const lastCheck = meta?.lastHealthCheck as string | undefined;
                    return (
                      <tr key={tenant.id}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-800">{tenant.name}</p>
                          <p className="text-xs text-slate-400">{tenant.slug}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                            healthStatus === 'healthy' ? 'bg-green-100 text-green-700'
                            : healthStatus ? 'bg-red-100 text-red-700'
                            : 'bg-slate-100 text-slate-500'
                          }`}>
                            {healthStatus ?? 'Not checked'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-600">
                          {chargesEnabled === true ? '✓ Enabled' : chargesEnabled === false ? '✗ Disabled' : '—'}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">
                          {lastCheck ? new Date(lastCheck).toLocaleString() : 'Never'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ─── GDPR Export Tab ────────────────────────────────────────── */}
        {activeTab === 'gdpr' && (
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="text-sm font-semibold text-slate-800 mb-1">GDPR Data Export</h3>
              <p className="text-xs text-slate-500 mb-4">
                Generate a full data export for a tenant (Art. 20 — Right to Data Portability). 
                Includes customers, subscriptions, invoices, payments, usage, credits, and audit logs.
              </p>

              <div className="overflow-hidden rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-500 uppercase">Tenant</th>
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                      <th className="px-4 py-2.5 text-right text-xs font-medium text-slate-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(tenantsData?.tenants ?? []).map((tenant) => (
                      <tr key={tenant.id}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-800">{tenant.name}</p>
                          <p className="text-xs text-slate-400">{tenant.id.slice(0, 8)}...</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                            tenant.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {tenant.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => { setExportTenantId(tenant.id); setShowExportConfirm(true); }}
                            className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
                          >
                            Export Data
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ─── Secret Rotation Tab ────────────────────────────────────── */}
        {activeTab === 'secrets' && (
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="text-sm font-semibold text-slate-800 mb-1">Provider Secret Rotation</h3>
              <p className="text-xs text-slate-500 mb-4">
                Rotate internal references and webhook signing keys for tenant provider configurations.
                Old secrets remain valid for 24 hours (overlap period).
              </p>

              <div className="overflow-hidden rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-500 uppercase">Tenant</th>
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-500 uppercase">Last Rotation</th>
                      <th className="px-4 py-2.5 text-right text-xs font-medium text-slate-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(tenantsData?.tenants ?? []).filter(t => t.status === 'active').map((tenant) => {
                      const meta = tenant.metadata as Record<string, unknown> | undefined;
                      const lastRotation = meta?.lastSecretRotation as string | undefined;
                      return (
                        <tr key={tenant.id}>
                          <td className="px-4 py-3">
                            <p className="font-medium text-slate-800">{tenant.name}</p>
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500">
                            {lastRotation ? new Date(lastRotation).toLocaleString() : 'Never rotated'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => { setRotateTenantId(tenant.id); setShowRotateConfirm(true); }}
                              className="rounded-lg border border-amber-200 px-3 py-1 text-xs font-medium text-amber-700 hover:bg-amber-50"
                            >
                              Rotate Secrets
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─── GDPR Confirmation ─────────────────────────────────────────── */}
      <ConfirmDialog
        open={showExportConfirm}
        title="Generate GDPR Export"
        message="This will generate a complete data export for the selected tenant. The file may be large and take a moment to generate."
        confirmLabel="Generate Export"
        variant="default"
        onConfirm={() => exportTenantId && gdprMutation.mutate(exportTenantId)}
        onCancel={() => setShowExportConfirm(false)}
        loading={gdprMutation.isPending}
      />

      {/* ─── Rotation Confirmation ─────────────────────────────────────── */}
      <ConfirmDialog
        open={showRotateConfirm}
        title="Rotate Provider Secrets"
        message="This will generate new internal webhook signing keys. Old keys remain valid for 24 hours. The tenant's Stripe API keys are NOT affected."
        confirmLabel="Rotate"
        variant="danger"
        onConfirm={() => rotateTenantId && rotateMutation.mutate(rotateTenantId)}
        onCancel={() => setShowRotateConfirm(false)}
        loading={rotateMutation.isPending}
      />
    </PlatformLayout>
  );
}
